<script lang="ts">
  /**
   * Full-screen, read-only board canvas for phones (ma-inzp, plans/mobile-v2.md §3.6).
   *
   * Pan and pinch-zoom only: nodes can't be dragged, selected, resized, edited or
   * deleted, and there is no toolbar, minimap or persistence. Projects open the
   * project rail. Rendered by the Boards page on top of the list; Close (or Esc)
   * returns to it.
   */
  import {
    SvelteFlow,
    Background,
    BackgroundVariant,
    type Node,
    type NodeTypes,
  } from '@xyflow/svelte';
  import '@xyflow/svelte/dist/style.css';
  import { get } from 'svelte/store';
  import { isProjectRailOpen } from '../../lib/stores/ui';
  import { initialCanvasViewport } from '../../lib/boardList';
  import type { BoardItem, BoardSummary, Project } from '../../lib/types';
  import { setBoardDisplay, type BoardDisplay } from '../../lib/boardContext';
  import { useBodyScrollLock } from '../../lib/useBodyScrollLock.svelte';
  import Icon from '../Icon.svelte';
  import BoardReadOnlyCard from './BoardReadOnlyCard.svelte';
  import BoardReadOnlyText from './BoardReadOnlyText.svelte';
  import ShapeNode from './ShapeNode.svelte';

  interface Props {
    board: BoardSummary;
    items: BoardItem[];
    projects: Project[];
    onClose: () => void;
  }

  let { board, items, projects, onClose }: Props = $props();

  const DEFAULT_SIZE = {
    project_card: { width: 260, height: 256 },
    shape: { width: 180, height: 130 },
    text: { width: 180, height: 40 },
  } as const;

  const nodeTypes: NodeTypes = {
    project_card: BoardReadOnlyCard as NodeTypes[string],
    text: BoardReadOnlyText as NodeTypes[string],
    shape: ShapeNode as NodeTypes[string],
  };

  // Read once: the overlay is mounted on demand and shows the board as it was
  // when opened. Zoom drives the card's inverse-scaled text.
  const display = $state<BoardDisplay>({ showCompleted: false, zoom: 1 });
  setBoardDisplay(display);

  // svelte-ignore state_referenced_locally
  const projectsById = new Map(projects.map((p) => [p.id, p]));

  // svelte-ignore state_referenced_locally
  const nodes: Node[] = items
    .filter((item) => item.kind !== 'project_card' || projectsById.has(item.projectId ?? ''))
    .map((item) => {
      const size = DEFAULT_SIZE[item.kind];
      return {
        id: item.id,
        type: item.kind,
        position: { x: item.x, y: item.y },
        width: item.width ?? size.width,
        height: item.height ?? size.height,
        zIndex: item.zIndex,
        data: {
          project: item.projectId ? projectsById.get(item.projectId) : undefined,
          content: item.content ?? '',
          style: item.style ?? {},
        },
      };
    });

  // svelte-ignore state_referenced_locally
  const initialViewport = initialCanvasViewport(
    nodes.map((node) => ({
      x: node.position.x,
      y: node.position.y,
      width: node.width ?? 0,
      height: node.height ?? 0,
    })),
    window.innerWidth,
  );
  // svelte-ignore state_referenced_locally
  display.zoom = initialViewport.zoom;

  useBodyScrollLock(() => true);

  let overlayEl = $state<HTMLDivElement | undefined>();
  let closeEl = $state<HTMLButtonElement | undefined>();

  // Focus: start on Close, keep Tab inside the overlay (the top bar and tab bar sit
  // behind it), and give focus back to whatever opened it (the "View canvas" button).
  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM side effect: focus move on mount, restored on unmount
  $effect(() => {
    const returnFocusTo = document.activeElement;
    closeEl?.focus({ preventScroll: true });
    return () => {
      // After the page has un-inerted its list (the opener is inside it), or focus() is ignored.
      queueMicrotask(() => {
        if (returnFocusTo instanceof HTMLElement && returnFocusTo.isConnected) {
          returnFocusTo.focus({ preventScroll: true });
        }
      });
    };
  });

  function focusableIn(container: HTMLElement): HTMLElement[] {
    return Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ),
    );
  }

  function handleKeydown(event: KeyboardEvent) {
    // A project rail opened from a card is on top while open and owns Esc and Tab. (Capture
    // phase: the rail closes itself on the same key, and must not look closed by then.)
    if (get(isProjectRailOpen)) return;

    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'Tab' && overlayEl) {
      const focusable = focusableIn(overlayEl);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !active || !overlayEl.contains(active);
      if (outside || (event.shiftKey && active === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
</script>

<svelte:window onkeydowncapture={handleKeydown} />

<div
  class="overlay"
  role="dialog"
  aria-modal="true"
  aria-label="{board.name} canvas, read-only"
  bind:this={overlayEl}
>
  <header class="bar">
    <button
      type="button"
      class="close"
      aria-label="Close canvas"
      bind:this={closeEl}
      onclick={onClose}
    >
      <Icon name="close" size={18} />
    </button>
    <div class="heading">
      <span class="name">{board.name}</span>
      <span class="hint">Read-only · drag to pan, pinch to zoom</span>
    </div>
  </header>

  <div class="flow">
    <SvelteFlow
      {nodes}
      {nodeTypes}
      {initialViewport}
      minZoom={0.1}
      maxZoom={2}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      nodesFocusable={false}
      edgesFocusable={false}
      zoomOnDoubleClick={false}
      panOnDrag
      deleteKey={null}
      proOptions={{ hideAttribution: true }}
      onmove={(_event, viewport) => (display.zoom = viewport.zoom)}
    >
      <Background variant={BackgroundVariant.Dots} gap={16} size={1} bgColor="#fafafa" />
    </SvelteFlow>
  </div>
</div>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 45;
    display: flex;
    flex-direction: column;
    background: #fafafa;
  }

  .bar {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
    min-height: 52px;
    padding: var(--safe-top, 0px) 8px 0 8px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
  }

  .close {
    position: relative;
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* 44px hit area around the 32px chip (sheet header standard, §3.4) */
  .close::after {
    content: '';
    position: absolute;
    inset: -6px;
  }

  .heading {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .name {
    overflow: hidden;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.25;
    color: #111827;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hint {
    font-size: 12px;
    color: #6b7280;
  }

  .flow {
    flex: 1 1 auto;
    min-height: 0;
  }
</style>
